import { HttpClient } from './httpClient.ts'

const INVALID_SPEECH_CHUNK = 'Invalid speech chunk'

export function throwIfSpeechCancelled(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Playback cancelled', 'AbortError')
}

function readSpeechEvent(line: string, onAudio: (pcm: Uint8Array) => void) {
  if (line.length > 65536) throw new Error(INVALID_SPEECH_CHUNK)
  const event = JSON.parse(line) as {
    audio?: string
    done?: boolean
    error?: string
  }
  if (event.error) throw new Error(event.error)
  if (event.done === true) return true
  if (typeof event.audio !== 'string') throw new Error(INVALID_SPEECH_CHUNK)
  onAudio(Uint8Array.from(atob(event.audio), (char) => char.charCodeAt(0)))
  return false
}

export class EdenaiApi {
  constructor(private httpClient: HttpClient) {}

  async teacherSpeech(
    chatId: number,
    messageId: number,
    signal: AbortSignal,
    onAudio: (pcm: Uint8Array) => void,
  ) {
    const response = await this.httpClient.postStream(
      `/api/text_to_speech/stream/${chatId}/${messageId}/`,
      signal,
    )

    if (
      !response.body ||
      !response.headers.get('content-type')?.includes('application/x-ndjson')
    ) {
      throw new Error('Invalid speech stream')
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let pending = ''
    try {
      for (;;) {
        const { value, done } = await reader.read()
        throwIfSpeechCancelled(signal)

        if (done) throw new Error('Speech stream ended before completion')
        pending += decoder.decode(value, { stream: true })
        let newline: number
        while ((newline = pending.indexOf('\n')) !== -1) {
          const line = pending.slice(0, newline)
          pending = pending.slice(newline + 1)
          if (readSpeechEvent(line, onAudio)) {
            return
          }
        }
        if (pending.length > 65536) throw new Error(INVALID_SPEECH_CHUNK)
      }
    } finally {
      await reader.cancel().catch(() => {
        // The reader may already be closed or cancelled.
      })
      reader.releaseLock()
    }
  }

  async speechToText(chatId: number, blob: Blob) {
    const form = new FormData()
    form.append('file', blob)
    return this.httpClient.request<{ text: string }>(
      `/api/speech_to_text/${chatId}/`,
      {
        body: form,
        method: 'POST',
      },
    )
  }

  async textToSpeech(chatId: number | undefined, text: string) {
    const path = chatId
      ? `/api/text_to_speech/${chatId}/`
      : `/api/text_to_speech/`

    const response = await this.httpClient.post<{ stream_key: string }>(path, {
      text,
    })

    const wav = await this.httpClient.getArrayBuffer(
      `/api/text_to_speech/download/${response.stream_key}/`,
    )

    return URL.createObjectURL(new Blob([wav], { type: 'audio/wav' }))
  }
}
